import type { MaterialityTopic, Sector } from '../../lib/assessment/types'
import { SECTOR_CORE_TOPICS, SECTOR_EXTENDED_TOPICS } from './sectorMappings'

/** Curated topic library — not exhaustive; users may add additional topics in the wizard. */
export const MATERIALITY_TOPICS: MaterialityTopic[] = [
  {
    id: 'climate_ghg',
    name: 'Climate change and GHG emissions',
    pillar: 'environmental',
    description: 'Direct and value-chain greenhouse gas emissions and climate-related transition risks.',
    potentialImpacts: 'Contribution to climate change; exposure to transition and physical climate risks.',
    financialRisksOpportunities: 'Energy costs, customer requirements, access to capital, operational disruption.',
    relevantSectors: 'all',
    uaeConsiderations:
      'UAE Federal Decree-Law No. 11 of 2024 establishes a federal climate framework. Whether detailed measurement/reporting duties apply depends on designation and implementing instruments — verify for your entity.',
    suggestedMetrics: ['Scope 1–3 GHG (tCO2e)', 'Energy intensity', 'Emission-reduction initiatives'],
    evidencePrompts: ['Fuel and electricity records', 'Fleet logs', 'Boundary documentation'],
    frameworks: ['GHG Protocol', 'IFRS S2', 'GRI 305'],
    displayPriority: 1,
    tier: 'core',
    businessEffectQuestion:
      'How much could climate change and greenhouse gas emissions affect your company’s costs, customers, financing, reputation, or ability to keep operating?',
    peoplePlanetQuestion:
      'How serious could your company’s greenhouse gas emissions be for people and the climate?',
  },
  {
    id: 'energy',
    name: 'Energy consumption and efficiency',
    pillar: 'environmental',
    description: 'Purchased and generated energy use and opportunities to improve efficiency.',
    potentialImpacts: 'Resource use and associated emissions from energy consumption.',
    financialRisksOpportunities: 'Utility cost volatility, efficiency savings, customer scorecards.',
    relevantSectors: 'all',
    uaeConsiderations: 'Cooling and electricity often dominate operational energy in the UAE climate.',
    suggestedMetrics: ['kWh consumed', 'Energy intensity per unit / m²'],
    evidencePrompts: ['Utility bills', 'Meter readings', 'Facility coverage register'],
    frameworks: ['GRI 302', 'IFRS S2'],
    displayPriority: 2,
    tier: 'core',
    businessEffectQuestion:
      'How much could energy use and efficiency affect your company’s bills, operations, or customer expectations?',
    peoplePlanetQuestion:
      'How serious could your company’s energy use be for the environment (including emissions from electricity and fuels)?',
  },
  {
    id: 'water',
    name: 'Water consumption and water stress',
    pillar: 'environmental',
    description: 'Water withdrawal, consumption and wastewater in a water-stressed region.',
    potentialImpacts: 'Pressure on scarce water resources; wastewater quality impacts.',
    financialRisksOpportunities: 'Utility costs, operational continuity, customer and lender expectations.',
    relevantSectors: 'all',
    uaeConsiderations: 'The UAE is highly water-stressed; desalination-linked energy use may be material for some operators.',
    suggestedMetrics: ['m³ withdrawn / consumed', 'Recycling rate'],
    evidencePrompts: ['Water bills', 'Site meters', 'Wastewater permits where applicable'],
    frameworks: ['GRI 303'],
    displayPriority: 3,
    tier: 'extended',
  },
  {
    id: 'waste',
    name: 'Waste and circularity',
    pillar: 'environmental',
    description: 'Operational waste generation, diversion and circularity practices.',
    potentialImpacts: 'Landfill and pollution impacts; resource inefficiency.',
    financialRisksOpportunities: 'Disposal costs, contractor performance, customer packaging requirements.',
    relevantSectors: 'all',
    uaeConsiderations: 'Emirate-level waste rules and contractor arrangements vary; do not assume one national practice.',
    suggestedMetrics: ['Tonnes generated', 'Diversion rate'],
    evidencePrompts: ['Waste contractor reports', 'Hazardous waste manifests'],
    frameworks: ['GRI 306'],
    displayPriority: 4,
    tier: 'extended',
  },
  {
    id: 'pollution',
    name: 'Pollution and releases',
    pillar: 'environmental',
    description: 'Air, water and soil releases, including hazardous substances where relevant.',
    potentialImpacts: 'Local environmental quality and community health impacts from releases.',
    financialRisksOpportunities: 'Permitting, remediation, operational downtime, customer specifications.',
    relevantSectors: ['manufacturing', 'construction', 'oil_gas_energy', 'agriculture', 'healthcare', 'logistics'],
    uaeConsiderations: 'Permit and monitoring expectations can differ by emirate, free zone and activity type.',
    suggestedMetrics: ['Permit exceedances', 'Hazardous substance inventory'],
    evidencePrompts: ['Monitoring reports', 'Spill logs', 'Permit conditions'],
    frameworks: ['GRI 305', 'GRI 306'],
    displayPriority: 5,
    tier: 'extended',
  },
  {
    id: 'biodiversity',
    name: 'Biodiversity and land use',
    pillar: 'environmental',
    description: 'Impacts on habitats, land conversion and ecosystem services.',
    potentialImpacts: 'Habitat degradation, species disturbance and land-use change impacts.',
    financialRisksOpportunities: 'Project approvals, lender safeguards, site development constraints.',
    relevantSectors: ['construction', 'oil_gas_energy', 'agriculture', 'real_estate', 'manufacturing'],
    uaeConsiderations: 'Coastal, desert and protected-area proximity can change risk profiles for projects and assets.',
    suggestedMetrics: ['Land disturbed', 'Sites near sensitive areas'],
    evidencePrompts: ['Site assessments', 'Project ESIA excerpts where available'],
    frameworks: ['GRI 304'],
    displayPriority: 6,
    tier: 'extended',
  },
  {
    id: 'resource_use',
    name: 'Resource use and raw materials',
    pillar: 'environmental',
    description: 'Materials intensity, critical inputs and circularity of resource use.',
    potentialImpacts: 'Resource depletion and upstream extraction impacts.',
    financialRisksOpportunities: 'Input price volatility, supplier concentration, embodied-carbon requests.',
    relevantSectors: ['manufacturing', 'construction', 'retail', 'agriculture', 'healthcare'],
    uaeConsiderations: 'Import dependence can make supplier data and embodied-carbon evidence harder to obtain.',
    suggestedMetrics: ['Material intensity', 'Recycled input share'],
    evidencePrompts: ['BOM / materials registers', 'Supplier declarations'],
    frameworks: ['GRI 301'],
    displayPriority: 7,
    tier: 'extended',
  },
  {
    id: 'refrigerants',
    name: 'Refrigerants and cooling',
    pillar: 'environmental',
    description: 'Fugitive emissions and management of refrigerants in cooling systems.',
    potentialImpacts: 'High global-warming-potential releases from leaks and servicing.',
    financialRisksOpportunities: 'Maintenance costs, equipment replacement, inventory completeness for GHG reporting.',
    relevantSectors: 'all',
    uaeConsiderations: 'Cooling intensity is typically high; refrigerant screening is often a readiness gap.',
    suggestedMetrics: ['Refrigerant charge inventory', 'Leak rates'],
    evidencePrompts: ['Equipment registers', 'Service records'],
    frameworks: ['GHG Protocol'],
    displayPriority: 8,
    tier: 'extended',
  },
  {
    id: 'climate_adaptation',
    name: 'Climate adaptation and resilience',
    pillar: 'environmental',
    description: 'Preparedness for physical climate risks affecting assets and operations.',
    potentialImpacts: 'Heat, flooding and other physical risks to people and assets.',
    financialRisksOpportunities: 'Business continuity, insurance, asset value.',
    relevantSectors: 'all',
    uaeConsiderations: 'Extreme heat and coastal exposure can be relevant depending on asset location.',
    suggestedMetrics: ['Risk assessments completed', 'Adaptation measures'],
    evidencePrompts: ['Site risk assessments', 'Business continuity plans'],
    frameworks: ['IFRS S2', 'TCFD-aligned practice'],
    displayPriority: 9,
    tier: 'extended',
  },
  {
    id: 'ohs',
    name: 'Occupational health and safety',
    pillar: 'social',
    description: 'Worker safety, incident prevention and safe working conditions.',
    potentialImpacts: 'Injuries, illness and wellbeing impacts on workers and contractors.',
    financialRisksOpportunities: 'Productivity, insurance, legal exposure, project continuity.',
    relevantSectors: 'all',
    uaeConsiderations: 'Heat exposure and contractor safety are common UAE operating considerations.',
    suggestedMetrics: ['LTIFR', 'Training hours', 'Near-miss reporting'],
    evidencePrompts: ['Incident logs', 'Training records', 'Contractor HSE requirements'],
    frameworks: ['GRI 403'],
    displayPriority: 10,
    tier: 'extended',
  },
  {
    id: 'heat_welfare',
    name: 'Heat exposure and worker welfare',
    pillar: 'social',
    description: 'Protection of workers from extreme heat and related welfare measures.',
    potentialImpacts: 'Heat stress, productivity loss and welfare harm for outdoor or industrial workers.',
    financialRisksOpportunities: 'Absenteeism, compliance risk, reputational exposure.',
    relevantSectors: ['construction', 'logistics', 'manufacturing', 'agriculture', 'oil_gas_energy', 'hospitality'],
    uaeConsiderations: 'Summer outdoor work controls are a distinctive regional operating issue.',
    suggestedMetrics: ['Heat-stress incidents', 'Rest-schedule compliance'],
    evidencePrompts: ['Shift policies', 'Site welfare audits'],
    frameworks: ['GRI 403'],
    displayPriority: 11,
    tier: 'extended',
  },
  {
    id: 'labour',
    name: 'Labour practices',
    pillar: 'social',
    description: 'Fair employment practices across own workforce and key contractors.',
    potentialImpacts: 'Working conditions, wages and contractor labour impacts.',
    financialRisksOpportunities: 'Retention, legal exposure, customer human-rights due diligence.',
    relevantSectors: 'all',
    uaeConsiderations: 'Workforce composition often includes contractors; map ownership of labour data carefully.',
    suggestedMetrics: ['Turnover', 'Contractor coverage', 'Grievance cases'],
    evidencePrompts: ['HR policies', 'Contractor clauses'],
    frameworks: ['GRI 401', 'GRI 414'],
    displayPriority: 12,
    tier: 'core',
  },
  {
    id: 'human_rights',
    name: 'Human rights in operations and supply chains',
    pillar: 'social',
    description: 'Salient human-rights risks in own operations and key supply-chain relationships.',
    potentialImpacts: 'Rights impacts on workers, communities and supply-chain participants.',
    financialRisksOpportunities: 'Customer due diligence, financing conditions, reputational exposure.',
    relevantSectors: 'all',
    uaeConsiderations: 'Multinational customers increasingly cascade human-rights questionnaires to UAE suppliers.',
    suggestedMetrics: ['Salient-risk assessments', 'Supplier screening coverage'],
    evidencePrompts: ['Due-diligence policy', 'Grievance mechanism evidence'],
    frameworks: ['UNGPs', 'GRI 412'],
    displayPriority: 13,
    tier: 'extended',
  },
  {
    id: 'employee_development',
    name: 'Employee development and retention',
    pillar: 'social',
    description: 'Skills development, career pathways and workforce retention.',
    potentialImpacts: 'Effects on employee opportunity, capability and job security.',
    financialRisksOpportunities: 'Hiring costs, productivity, knowledge continuity.',
    relevantSectors: 'all',
    uaeConsiderations: 'Competitive labour markets and visa-linked employment can affect retention dynamics.',
    suggestedMetrics: ['Training hours', 'Voluntary turnover'],
    evidencePrompts: ['Learning records', 'Exit analysis'],
    frameworks: ['GRI 404'],
    displayPriority: 14,
    tier: 'extended',
  },
  {
    id: 'dei',
    name: 'Diversity, equity and inclusion',
    pillar: 'social',
    description: 'Representation, inclusion practices and equitable opportunity.',
    potentialImpacts: 'Inclusion or exclusion effects across workforce segments.',
    financialRisksOpportunities: 'Talent attraction, customer expectations, governance scoring.',
    relevantSectors: 'all',
    uaeConsiderations: 'Disclosure expectations vary; focus on accurate, locally lawful workforce metrics.',
    suggestedMetrics: ['Representation indicators', 'Pay-equity review status'],
    evidencePrompts: ['HR dashboards', 'Inclusion policy'],
    frameworks: ['GRI 405'],
    displayPriority: 15,
    tier: 'extended',
  },
  {
    id: 'customer_health_safety',
    name: 'Customer health and safety',
    pillar: 'social',
    description: 'Product/service safety and customer wellbeing impacts.',
    potentialImpacts: 'Injury, illness or safety incidents affecting customers or end users.',
    financialRisksOpportunities: 'Liability, recalls, brand trust, regulatory scrutiny.',
    relevantSectors: ['healthcare', 'hospitality', 'retail', 'manufacturing', 'real_estate', 'agriculture'],
    uaeConsiderations: 'Sector licensing and facility standards may intersect with customer-safety evidence needs.',
    suggestedMetrics: ['Incident rates', 'Product safety non-conformances'],
    evidencePrompts: ['Incident logs', 'Quality/safety certifications'],
    frameworks: ['GRI 416'],
    displayPriority: 16,
    tier: 'extended',
  },
  {
    id: 'community',
    name: 'Community impacts',
    pillar: 'social',
    description: 'Effects of operations on neighbouring communities and local stakeholders.',
    potentialImpacts: 'Noise, traffic, land use, employment and community wellbeing effects.',
    financialRisksOpportunities: 'Project delays, licence-to-operate, stakeholder conflict.',
    relevantSectors: ['construction', 'oil_gas_energy', 'manufacturing', 'real_estate', 'agriculture', 'logistics'],
    uaeConsiderations: 'Community expectations can differ across mainland, free-zone and project contexts.',
    suggestedMetrics: ['Grievances', 'Local employment share'],
    evidencePrompts: ['Stakeholder logs', 'Community investment records'],
    frameworks: ['GRI 413'],
    displayPriority: 17,
    tier: 'extended',
  },
  {
    id: 'data_privacy',
    name: 'Data privacy and customer protection',
    pillar: 'social',
    description: 'Protection of personal and sensitive customer or employee data.',
    potentialImpacts: 'Privacy harms from misuse, breach or inadequate controls.',
    financialRisksOpportunities: 'Breach costs, customer trust, contractual requirements.',
    relevantSectors: ['technology_services', 'financial_services', 'healthcare', 'retail', 'hospitality'],
    uaeConsiderations: 'UAE data-protection expectations and sector rules should be verified separately from ESG disclosures.',
    suggestedMetrics: ['Incidents', 'Training coverage', 'DPIA completion where used'],
    evidencePrompts: ['Privacy notices', 'Incident response records'],
    frameworks: ['GRI 418'],
    displayPriority: 18,
    tier: 'extended',
  },
  {
    id: 'ethics',
    name: 'Business ethics and anti-corruption',
    pillar: 'governance',
    description: 'Integrity controls, anti-corruption and ethical conduct.',
    potentialImpacts: 'Corruption and unethical conduct risks affecting stakeholders and markets.',
    financialRisksOpportunities: 'Fines, debarment, lender and investor trust.',
    relevantSectors: 'all',
    uaeConsiderations: 'Listed and multinational-facing companies often face heightened due-diligence expectations.',
    suggestedMetrics: ['Training coverage', 'Confirmed incidents'],
    evidencePrompts: ['Code of conduct', 'Whistleblowing channel'],
    frameworks: ['GRI 205'],
    displayPriority: 19,
    tier: 'core',
  },
  {
    id: 'board_oversight',
    name: 'Board oversight and accountability',
    pillar: 'governance',
    description: 'Board or owner oversight of sustainability and climate-related issues.',
    potentialImpacts: 'Weak oversight can allow unmanaged impacts and weak disclosures.',
    financialRisksOpportunities: 'Investor confidence, strategy alignment, escalation of ESG risks.',
    relevantSectors: 'all',
    uaeConsiderations: 'Listed issuers often face clearer governance disclosure expectations than private SMEs.',
    suggestedMetrics: ['Agenda coverage', 'Named accountability'],
    evidencePrompts: ['Board minutes excerpts', 'Terms of reference'],
    frameworks: ['IFRS S1', 'GRI 2'],
    displayPriority: 20,
    tier: 'extended',
  },
  {
    id: 'risk_management',
    name: 'Risk management',
    pillar: 'governance',
    description: 'Integration of sustainability and climate risks into enterprise risk processes.',
    potentialImpacts: 'Unmanaged ESG risks can escalate into operational or stakeholder harm.',
    financialRisksOpportunities: 'Loss avoidance, insurance, capital access.',
    relevantSectors: 'all',
    uaeConsiderations: 'Customer and lender questionnaires often ask whether climate/ESG risks are formally assessed.',
    suggestedMetrics: ['Risk register coverage', 'Review cadence'],
    evidencePrompts: ['Risk register', 'Scenario or heat maps'],
    frameworks: ['IFRS S1', 'IFRS S2'],
    displayPriority: 21,
    tier: 'extended',
  },
  {
    id: 'data_governance',
    name: 'Data governance and reporting controls',
    pillar: 'governance',
    description: 'Controls over ESG/GHG data quality, documentation and review.',
    potentialImpacts: 'Misstated disclosures can mislead stakeholders.',
    financialRisksOpportunities: 'Reporting rework, assurance readiness, customer questionnaire friction.',
    relevantSectors: 'all',
    uaeConsiderations: 'Exchange and customer questionnaires often require evidence trail, not only narrative claims.',
    suggestedMetrics: ['Documented methods', 'Review completion rate'],
    evidencePrompts: ['Calculation files', 'Emission-factor register', 'Sign-off records'],
    frameworks: ['GHG Protocol', 'IFRS S1'],
    displayPriority: 22,
    tier: 'core',
  },
  {
    id: 'procurement',
    name: 'Responsible procurement',
    pillar: 'governance',
    description: 'Environmental and social expectations in purchasing and supplier management.',
    potentialImpacts: 'Upstream impacts embedded in purchased goods and services.',
    financialRisksOpportunities: 'Supply disruption, customer cascade requirements, Scope 3 data gaps.',
    relevantSectors: 'all',
    uaeConsiderations: 'Import-heavy supply chains may concentrate Scope 3 data challenges.',
    suggestedMetrics: ['Supplier ESG screening coverage', 'Critical-supplier assessments'],
    evidencePrompts: ['Procurement policy', 'Supplier questionnaires'],
    frameworks: ['GRI 204', 'GRI 308', 'GRI 414'],
    displayPriority: 23,
    tier: 'extended',
  },
  {
    id: 'regulatory_management',
    name: 'Regulatory management',
    pillar: 'governance',
    description: 'Processes to track environmental, climate and sustainability-related regulatory change.',
    potentialImpacts: 'Non-compliance can harm people, environment and market trust.',
    financialRisksOpportunities: 'Penalties, delayed permits, unexpected reporting burden.',
    relevantSectors: 'all',
    uaeConsiderations:
      'Federal and emirate competent authorities may both be relevant. Free-zone establishments should confirm which authority applies.',
    suggestedMetrics: ['Regulatory register updates', 'Open actions'],
    evidencePrompts: ['Compliance calendar', 'Authority correspondence log'],
    frameworks: ['Internal control practice'],
    displayPriority: 24,
    tier: 'extended',
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity and information security',
    pillar: 'governance',
    description: 'Protection of systems and information supporting operations and reporting.',
    potentialImpacts: 'Breaches can harm customers, employees and critical services.',
    financialRisksOpportunities: 'Downtime, incident response cost, customer trust.',
    relevantSectors: 'all',
    uaeConsiderations: 'Increasingly requested in supplier due diligence alongside ESG topics.',
    suggestedMetrics: ['Incidents', 'Control coverage', 'Training completion'],
    evidencePrompts: ['Security policy', 'Incident logs'],
    frameworks: ['Governance / security frameworks as adopted'],
    displayPriority: 25,
    tier: 'extended',
  },
  {
    id: 'transparency_claims',
    name: 'Transparency and responsible sustainability claims',
    pillar: 'governance',
    description: 'Accuracy of public sustainability claims, labels and marketing statements.',
    potentialImpacts: 'Misleading claims can undermine stakeholder trust.',
    financialRisksOpportunities: 'Greenwashing allegations, customer disputes, reputational damage.',
    relevantSectors: 'all',
    uaeConsiderations: 'Marketing and tender claims should be reconcilable to evidence held by the company.',
    suggestedMetrics: ['Claim review checklist completion', 'Substantiation coverage'],
    evidencePrompts: ['Claim register', 'Supporting calculations'],
    frameworks: ['Advertising / claims guidance as applicable', 'GRI 2'],
    displayPriority: 26,
    tier: 'extended',
  },
]

// Fix accidental invalid sector in customer_health_safety - I used 'food' as never which is wrong
// I'll fix via search replace after write
function topicById(id: string): MaterialityTopic | undefined {
  return MATERIALITY_TOPICS.find((t) => t.id === id)
}

export function topicsForSector(
  sector: string,
  options: { includeExtended?: boolean } = { includeExtended: true },
): MaterialityTopic[] {
  const s = (sector || 'other') as Sector
  const coreIds = SECTOR_CORE_TOPICS[s] || SECTOR_CORE_TOPICS.other
  const extendedIds = SECTOR_EXTENDED_TOPICS[s] || SECTOR_EXTENDED_TOPICS.other
  const ids = options.includeExtended === false ? coreIds : [...coreIds, ...extendedIds]
  const seen = new Set<string>()
  const topics: MaterialityTopic[] = []
  for (const id of ids) {
    if (seen.has(id)) continue
    const topic = topicById(id)
    if (!topic) continue
    seen.add(id)
    topics.push({
      ...topic,
      tier: coreIds.includes(id) ? 'core' : 'extended',
    })
  }
  return topics
}

export function coreTopicsForSector(sector: string): MaterialityTopic[] {
  return topicsForSector(sector, { includeExtended: false })
}

export function extendedTopicsForSector(sector: string): MaterialityTopic[] {
  const s = (sector || 'other') as Sector
  const core = new Set(SECTOR_CORE_TOPICS[s] || SECTOR_CORE_TOPICS.other)
  return topicsForSector(sector, { includeExtended: true }).filter((t) => !core.has(t.id))
}
